import { Trash2Icon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import { resolvePageUrl } from './page-url';
import TeaserSections from './TeaserSections';
import { TeaserHostForm } from './types';
import PreviewButton from '../../../WebsitePreview/PreviewButton';

type Props = {
  index: number;
  /** The accordion value for this row — its RHF field id, which is stable. */
  value: string;
  readOnly?: boolean;
  onRemove: () => void;
  deskId?: string;
  integrationId?: string;
};

/**
 * One `teaser.pages[]` entry, as a collapsible row.
 *
 * The address and the author's instructions are not editable here: a page is
 * created from an address in `AddWebpageCard` and this is what it became. What
 * is left to author is the actions per section, which is what the row opens on.
 *
 * The position is no longer shown. `pages[]` order is still the matching
 * semantics — first entry whose `url` appears in the visitor's URL wins — but
 * the rows are appended and never reordered, so numbering them told the author
 * about a rule they could not act on.
 */
const TeaserPageCard = ({ index, value, readOnly, onRemove, deskId, integrationId }: Props) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TeaserHostForm>();

  const websiteUrl = useWatch({ control, name: 'teaser.website_url' }) ?? '';
  const hint = useWatch({ control, name: `teaser.pages.${index}.url` }) ?? '';
  const title = useWatch({ control, name: `teaser.pages.${index}.title` }) ?? '';

  /*
    What the preview opens, and the address the header shows.

    `null` when the row cannot produce one — which is only reachable for a page
    saved before the site was recorded, since every row created here is split
    from an absolute address.
  */
  const resolvedUrl = resolvePageUrl(hint, websiteUrl);

  return (
    <AccordionItem value={value} className="rounded-lg border border-stroke bg-bg-muted-light p-1">
      <AccordionTrigger className="items-center rounded-md px-2 py-1.5 hover:bg-bg-muted-50 hover:no-underline">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm leading-5 font-medium text-foreground">
            {title || t('integrations.web_teaser.pages.untitled')}
          </span>
          <span className="truncate text-xs leading-4 text-foreground-muted">
            {resolvedUrl ?? hint}
          </span>
        </div>
      </AccordionTrigger>

      <AccordionContent className="flex flex-col gap-6 px-2 pt-4 pb-5">
        <div className="flex items-center gap-6">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-xs leading-4 font-medium text-foreground">
              {t('integrations.web_teaser.pages.quick_actions')}
            </span>
            <span className="truncate text-xs leading-4 text-foreground-muted">
              {t('integrations.web_teaser.pages.quick_actions_help')}
            </span>
          </div>
          <PreviewButton
            url={resolvedUrl}
            deskId={deskId}
            integrationId={integrationId}
            disabled={readOnly}
          />
        </div>

        <Separator />

        <TeaserSections pageIndex={index} readOnly={readOnly} />

        {!readOnly && (
          <Button
            type="button"
            variant="destructive-ghost"
            size="sm"
            className="self-start"
            onClick={onRemove}
          >
            <Trash2Icon size={16} />
            {t('integrations.web_teaser.pages.delete')}
          </Button>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

export default TeaserPageCard;
